<script lang="ts">
	import defaultOgImage from "@assets/images/og-image.png?webp&h=630&imagetools";
	import AuthenticationModal from "@components/AuthenticationModal.svelte";
	import CartModal from "@components/CartModal.svelte";
	import SubscriptionConfiguratorModal from "@components/SubscriptionConfiguratorModal.svelte";
	import CartOverviewModal from "@components/cart/CartOverviewModal.svelte";
	import BreadcrumbListStructuredData from "@components/common/BreadcrumbListStructuredData.svelte";
	import StructuredData from "@components/common/StructuredData.svelte";
	import { Auth } from "@utils/Auth.svelte.js";
	import { createTitle } from "@utils/createTitle.js";
	import { getCookie } from "@utils/getCookie.js";
	import { setBodyScrollLock } from "@utils/setBodyScrollLock.js";
	import { type Snippet, onMount } from "svelte";
	import InterVariableFont from "@core/assets/fonts/Inter/InterVariable.woff2";
	import { defaultTenant } from "@core/config/defaultTenant.js";
	import { tenants } from "@core/config/tenants.js";
	import { Country } from "@core/schema/Country.js";
	import { Currency } from "@core/schema/Currency.js";
	import { EconomicSubjectVatType } from "@core/schema/EconomicSubjectVatType.js";
	import type { Media } from "@core/schema/Media.js";
	import { appName } from "@core/schema/names/appName.js";
	import { isEnum } from "@core/utils/enum/isEnum.js";
	import { thumbUploadedFile } from "@core/utils/thumbUploadedFile.js";
	import "../../assets/css/main.css";
	import { Cart } from "../../cart/Cart.svelte.js";
	import { getGlobalSettings } from "../../contexts/getGlobalSettings.js";
	import { getGlobalTemplateProps } from "../../contexts/getGlobalTemplateProps.js";
	import { currencyCookieName } from "../../currencyCookieName.js";
	import { defaultDestinationCountry } from "../../defaultDestinationCountry.js";
	import { destinationCountryCookieName } from "../../destinationCountryCookieName.js";
	import {
		buildOrganization,
		buildReturnPolicies,
		buildShippingDetails,
		buildWebSite,
	} from "../../seo/sharedEntities.js";
	import { lockScroll } from "../../state/lockScroll.svelte.js";
	import { selectedCurrency } from "../../state/selectedCurrency.svelte.js";
	import { selectedDestinationCountry } from "../../state/selectedDestinationCountry.svelte.js";
	import Breadcrumbs from "./Breadcrumbs.svelte";
	import Cookies from "./Cookies.svelte";
	import Footer from "./Footer.svelte";
	import GoogleAnalytics from "./GoogleAnalytics.svelte";
	import HeadFavicons from "./HeadFavicons.svelte";
	import Header from "./Header.svelte";

	interface PreloadFont {
		type: string;
		href: string;
	}

	const {
		ogImage = defaultOgImage,
		children,
		transparentHeader = false,
		pageHasMobileBottomBar = false,
	}: {
		ogImage?: string | Media;
		children: Snippet;
		transparentHeader?: boolean;
		pageHasMobileBottomBar?: boolean;
	} = $props();

	const settings = getGlobalSettings();
	const { WEB_URL_OR_LOCAL, FILE_BUCKET_URL_MAP, ROBOTS } = settings;
	const globalTemplateProps = getGlobalTemplateProps();
	const {
		breadcrumbs,
		countrySettings,
		currency,
		description,
		destinationCountry,
		index,
		infoBannerMessages,
		pagePath,
		pricingLevel,
		subcategories,
		tenant,
		title,
		exchangeRatesSetting,
		mothersBlendProduct,
		cafeinaProduct,
	} = globalTemplateProps;

	const initAuth = Auth.init(settings, globalTemplateProps);

	const pageTitle = createTitle(title);
	const hasInfoBannerMessages = infoBannerMessages.length > 0;
	const fontsToPreload: PreloadFont[] = [{ type: "font/woff2", href: InterVariableFont }];
	const canonical = tenant.locale in pagePath ? `${WEB_URL_OR_LOCAL}${pagePath[tenant.locale]}` : undefined;
	const themeColor = "#5E2828";
	const ogImagePath = $derived.by(() => {
		if (typeof ogImage === "object") {
			return thumbUploadedFile(1200, 630, ogImage, FILE_BUCKET_URL_MAP, null);
		}
		return `${WEB_URL_OR_LOCAL}${ogImage}`;
	});
	const organizationSchema = $derived(buildOrganization(WEB_URL_OR_LOCAL, countrySettings));
	const webSiteSchema = $derived(buildWebSite(WEB_URL_OR_LOCAL));
	const returnPolicies = $derived(buildReturnPolicies(WEB_URL_OR_LOCAL, countrySettings));
	const shippingDetails = $derived(
		buildShippingDetails(
			WEB_URL_OR_LOCAL,
			countrySettings,
			tenant.defaultCurrency,
			exchangeRatesSetting?.exchangeRates ?? [],
		),
	);

	$effect(() => {
		setBodyScrollLock(lockScroll.locked);
	});

	selectedCurrency.current = currency !== "fromCookies" ? currency : null;
	selectedDestinationCountry.current = destinationCountry !== "fromCookies" ? destinationCountry : null;

	onMount(async () => {
		let finalDestinationCountry = destinationCountry;
		const destinationFromCookie = await getCookie(destinationCountryCookieName);
		if (isEnum(Country)(destinationFromCookie)) {
			finalDestinationCountry = destinationFromCookie;
		}
		if (finalDestinationCountry === "fromCookies") {
			finalDestinationCountry = defaultDestinationCountry;
		}
		selectedDestinationCountry.current = finalDestinationCountry;
		let finalCurrency = currency;
		const currencyfromCookie = await getCookie(currencyCookieName);
		if (isEnum(Currency)(currencyfromCookie)) {
			finalCurrency = currencyfromCookie;
		}
		if (finalCurrency === "fromCookies") {
			finalCurrency = tenant.defaultCurrency;
		}
		selectedCurrency.current = finalCurrency;

		await initAuth;
		await Cart.init(settings, {
			destinationCountry: finalDestinationCountry,
			currency: finalCurrency,
			countrySettings,
			pricingLevel,
			tenant,
			subcategories,
			exchangeRates: exchangeRatesSetting?.exchangeRates ?? [],
			vatType: EconomicSubjectVatType.NotVatSubject,
			mothersBlendProduct,
			cafeinaProduct,
		});
	});
</script>

<svelte:head>
	<!-- Preloads of fonts used at the top of pages -->
	{#each fontsToPreload as { href, type } (href)}
		<link rel="preload" as="font" {href} {type} crossorigin="anonymous" />
	{/each}

	<!-- Theme color -->
	<meta name="msapplication-TileColor" content={themeColor} />
	<meta name="theme-color" content={themeColor} />

	<!-- Site name -->
	<meta property="og:site_name" content={appName} />
	<meta name="twitter:site" content={appName} />
	<meta name="application-name" content={appName} />
	<meta name="apple-mobile-web-app-title" content={appName} />

	<!-- Dynamic Open graph tags (Meta) -->
	{#if canonical}
		<meta property="og:url" content={canonical} />
	{/if}
	<meta property="og:image" content={ogImagePath} />

	<!-- Dynamic Twitter Meta tags -->
	<meta name="twitter:card" content="summary_large_image" />
	<meta property="twitter:domain" content={WEB_URL_OR_LOCAL} />
	{#if canonical}
		<meta property="twitter:url" content={canonical} />
	{/if}
	<meta name="twitter:image" content={ogImagePath} />

	<!-- Title and description -->
	<title>{pageTitle}</title>
	<meta property="title" content={pageTitle} />
	<meta property="og:title" content={pageTitle} />
	<meta name="twitter:title" content={pageTitle} />
	<meta name="description" content={description} />
	<meta name="og:description" content={description} />
	<meta name="twitter:description" content={description} />

	<!-- Robots -->
	<meta name="robots" content={ROBOTS === "index" && index ? "index, follow" : "noindex, nofollow"} />

	<!-- Canonical and hreflang -->
	{#if canonical}
		<link rel="canonical" href={canonical} />
	{/if}
	{#if defaultTenant.locale in pagePath}
		<link rel="alternate" hreflang="x-default" href="{WEB_URL_OR_LOCAL}{pagePath[defaultTenant.locale]}" />
	{/if}
	{#each Object.values(tenants) as tenant (tenant.locale)}
		{#if tenant.locale in pagePath}
			<link rel="alternate" hreflang={tenant.locale} href="{WEB_URL_OR_LOCAL}{pagePath[tenant.locale]}" />
		{/if}
	{/each}
</svelte:head>

<HeadFavicons />

<StructuredData data={organizationSchema} />
<StructuredData data={webSiteSchema} />
{#each returnPolicies as policy (policy["@id"])}
	<StructuredData data={policy} />
{/each}
{#each shippingDetails as detail (detail["@id"])}
	<StructuredData data={detail} />
{/each}
<BreadcrumbListStructuredData {breadcrumbs} canonicalUrl={canonical} />

<GoogleAnalytics />

<div class={["flex flex-col min-h-full", !hasInfoBannerMessages && "max-sm:[--info-banner-height:0px]"]}>
	<Header transparent={transparentHeader} />

	<main class="grid-container grow relative">
		<Breadcrumbs {breadcrumbs} currentPath={pagePath[tenant.locale]} />

		{@render children()}
	</main>

	<Footer {pageHasMobileBottomBar} />
</div>

{#if Cart.loaded}
	<SubscriptionConfiguratorModal bind:show={Cart.showSubscriptionConfigurator} />
{/if}
<AuthenticationModal />
<CartModal />
<CartOverviewModal />
<Cookies />
